import React, { useEffect, useRef, useState } from "react";
import Footer from "../component/Footer";
import Navbar from "../component/Navbar";
import { SiUnitednations } from "react-icons/si";
import {
  HiOutlineDocumentText,
  HiOutlineGlobeAlt,
  HiOutlineChartBar,
  HiOutlineLightBulb,
  HiOutlineAcademicCap,
  HiOutlineCog,
  HiOutlineScale,
  HiOutlineUserGroup,
} from "react-icons/hi";

// ---------- Reusable animated counter ----------
const Counter = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={countRef}>{count.toLocaleString()}</span>;
};

// ---------- Stat Card ----------
const StatCard = ({ value, label, suffix = "" }) => (
  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-gray-100 text-center hover:scale-105 transition-transform duration-300">
    <div className="text-4xl font-light text-[rgb(43,147,208)]">
      <Counter end={value} />
      {suffix}
    </div>
    <p className="text-sm text-gray-600 mt-2">{label}</p>
  </div>
);

// ---------- Logo Card for UN Agencies ----------
const AgencyLogo = ({ Icon, name, description }) => (
  <div className="group relative bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[rgb(43,147,208)] cursor-default">
    <div className="text-5xl text-[rgb(43,147,208)] mb-3 flex justify-center">
      <Icon />
    </div>
    <h4 className="text-center font-medium text-gray-800">{name}</h4>
    <div className="absolute inset-0 bg-[rgb(43,147,208)] text-white rounded-xl p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center text-xs">
      {description}
    </div>
  </div>
);

// ---------- Partner Card for International Bodies ----------
const PartnerCard = ({ name, description }) => (
  <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 shadow-md hover:shadow-2xl transition-all duration-300 border-l-4 border-[#f5e56b] hover:border-[rgb(43,147,208)] group">
    <h4 className="font-semibold text-gray-800 mb-2 group-hover:text-[rgb(43,147,208)] transition-colors">
      {name}
    </h4>
    <p className="text-sm text-gray-600">{description}</p>
  </div>
);

// ---------- SDG Pill with Tooltip ----------
const SDGPill = ({ goal, title, color }) => {
  const [showTip, setShowTip] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      <div
        className={`px-4 py-2 rounded-full text-sm font-medium cursor-default transition-all duration-200 ${color}`}
      >
        {goal}
      </div>
      {showTip && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-10">
          {title}
        </div>
      )}
    </div>
  );
};

// ---------- Main Page Component ----------
const WASMEPartnershipPage = () => {
  // Data for UN agencies – icons drawn from heroicons
  const unAgencies = [
    {
      icon: SiUnitednations,
      name: "United Nations (ECOSOC)",
      desc: "Consultative status since 1985, representing MSMEs in global policy dialogues.",
    },
    {
      icon: HiOutlineGlobeAlt,
      name: "UNCTAD",
      desc: "Contributing to trade and development reports, and investment guidelines.",
    },
    {
      icon: HiOutlineAcademicCap,
      name: "UNESCO",
      desc: "Promoting inclusive education and knowledge sharing for entrepreneurs.",
    },
    {
      icon: HiOutlineCog,
      name: "UNIDO",
      desc: "Partnering on industrial development and sustainable supply chains.",
    },
    {
      icon: HiOutlineScale,
      name: "WIPO",
      desc: "Advocating for IP awareness and technology transfer among MSMEs.",
    },
    {
      icon: HiOutlineUserGroup,
      name: "ILO",
      desc: "Shaping policies for decent work and youth employment.",
    },
  ];

  const internationalPartners = [
    {
      name: "World Customs Organization (WCO)",
      desc: "Simplifying trade procedures for small exporters.",
    },
    {
      name: "OECD",
      desc: "Feeding MSME perspectives into economic policy recommendations.",
    },
    {
      name: "International Council for Small Business (ICSB)",
      desc: "Joint research and global conferences.",
    },
    {
      name: "Asia-Pacific Economic Cooperation (APEC)",
      desc: "SME working group contributions.",
    },
    {
      name: "Asian and Pacific Centre for Transfer of Technology (APCTT)",
      desc: "Technology transfer initiatives.",
    },
  ];

  const impactPoints = [
    {
      icon: <HiOutlineDocumentText className="w-8 h-8" />,
      title: "Policy Advocacy",
      desc: "Advocating for MSMEs-friendly global policies",
    },
    {
      icon: <HiOutlineGlobeAlt className="w-8 h-8" />,
      title: "Multilateral Dialogues",
      desc: "Contributing to inclusive trade and sustainable development",
    },
    {
      icon: <HiOutlineChartBar className="w-8 h-8" />,
      title: "Capacity Building",
      desc: "Knowledge exchange across borders",
    },
    {
      icon: <HiOutlineLightBulb className="w-8 h-8" />,
      title: "Innovation & SDGs",
      desc: "IP awareness, tech transfer, SDG alignment",
    },
  ];

  const newsItems = [
    {
      title: "WASME at UNCTAD eCommerce Week 2025",
      date: "15 Apr 2025",
      desc: "Advocating for digital inclusion of MSMEs.",
    },
    {
      title: "New MoU with UNESCO on skills development",
      date: "02 Mar 2025",
      desc: "Joint program for youth entrepreneurship.",
    },
    {
      title: "Statement delivered at ECOSOC High-Level Segment",
      date: "18 Feb 2025",
      desc: "Call for MSME-friendly financing frameworks.",
    },
  ];

  const events = [
    { title: "Global MSME Summit 2025", date: "12-14 June", location: "Geneva" },
    { title: "SDG Business Forum", date: "17 July", location: "New York (side event)" },
  ];

  // Refs for scroll animations
  const sectionsRef = useRef([]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-10");
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -50px 0px" }
    );
    sectionsRef.current.forEach((ref) => ref && observer.observe(ref));
    return () => observer.disconnect();
  }, []);

  const addToRefs = (el) => {
    if (el && !sectionsRef.current.includes(el)) sectionsRef.current.push(el);
  };

  return (
    <>
      <Navbar />
      <div className="bg-white font-sans text-gray-700 antialiased overflow-x-hidden">
        {/* Hero with animated world map background */}
        <div className="relative bg-gradient-to-br from-[#f0f7ff] via-[#f7fafd] to-[#fff9e6] py-20 md:py-28 overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Floating gradient orbs */}
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-[rgb(43,147,208)] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#f5e56b] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[rgb(43,147,208)] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>

            {/* Enhanced decorative SVG with animations */}
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 800 400"
              preserveAspectRatio="none"
            >
              {/* <defs>
                <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgb(43,147,208)" stopOpacity="0.3" />
                  <stop offset="50%" stopColor="#f5a800" stopOpacity="0.2" />
                  <stop offset="100%" stopColor="rgb(43,147,208)" stopOpacity="0.3" />
                </linearGradient>
                <linearGradient id="circleGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgb(43,147,208)" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#f5a800" stopOpacity="0.1" />
                </linearGradient>
                <linearGradient id="circleGradient2" x1="100%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#f5a800" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="rgb(43,147,208)" stopOpacity="0.1" />
                </linearGradient>
              </defs> */}

              {/* Animated wave lines */}
              <g className="animate-wave">
                <path
                  d="M0,200 Q200,150 400,200 T800,200"
                  stroke="url(#waveGradient)"
                  fill="none"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <path
                  d="M0,220 Q200,170 400,220 T800,220"
                  stroke="url(#waveGradient)"
                  fill="none"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  opacity="0.5"
                />
              </g>

              {/* Animated circles */}
              <g>
                <circle cx="150" cy="180" r="25" fill="url(#circleGradient1)" className="animate-float">
                  <animate attributeName="r" values="25;30;25" dur="4s" repeatCount="indefinite" />
                </circle>
                <circle cx="450" cy="220" r="35" fill="url(#circleGradient2)" className="animate-float animation-delay-1000">
                  <animate attributeName="r" values="35;40;35" dur="5s" repeatCount="indefinite" />
                </circle>
                <circle cx="650" cy="160" r="28" fill="url(#circleGradient1)" className="animate-float animation-delay-2000">
                  <animate attributeName="r" values="28;33;28" dur="4.5s" repeatCount="indefinite" />
                </circle>
                <circle cx="320" cy="300" r="15" fill="rgb(43,147,208)" opacity="0.15" className="animate-float animation-delay-1500">
                  <animate attributeName="r" values="15;20;15" dur="3.5s" repeatCount="indefinite" />
                </circle>
                <circle cx="580" cy="280" r="18" fill="#f5a800" opacity="0.15" className="animate-float animation-delay-2500">
                  <animate attributeName="r" values="18;23;18" dur="4s" repeatCount="indefinite" />
                </circle>
              </g>

              {/* Decorative dots pattern */}
              <g opacity="0.1">
                {[...Array(12)].map((_, i) => (
                  <circle
                    key={i}
                    cx={50 + i * 70}
                    cy={350}
                    r="2"
                    fill="rgb(43,147,208)"
                  />
                ))}
                {[...Array(8)].map((_, i) => (
                  <circle
                    key={i + 12}
                    cx={100 + i * 90}
                    cy={80}
                    r="1.5"
                    fill="#f5a800"
                  />
                ))}
              </g>
            </svg>
          </div>

          {/* Main Content Container */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center md:text-left md:flex md:items-center md:justify-between">
              <div className="relative">
                {/* Subtle glow behind heading */}
                <div className="absolute inset-0 bg-gradient-to-r from-[rgb(43,147,208)] to-[#f5a800] blur-2xl opacity-20 rounded-full scale-150 pointer-events-none"></div>

                {/* Heading with enhanced styling */}
                <h1 className="text-5xl sm:text-6xl md:text-5xl lg:text-7xl font-normal leading-tight tracking-tight relative">
                  <span className="bg-gradient-to-r from-[rgb(43,147,208)] to-[rgb(43,147,208)] bg-clip-text text-transparent drop-shadow-sm">
                    WASME
                  </span>
                  <span className="text-[#f5a800] mx-2 inline-block transform hover:scale-110 transition-transform duration-300">&amp;</span>
                  <span className="bg-gradient-to-r from-[rgb(43,147,208)] to-[rgb(43,147,208)] bg-clip-text text-transparent drop-shadow-sm">
                    the UN
                  </span>
                </h1>

                {/* Decorative underline */}
                <div className="hidden md:block h-1 w-24 bg-gradient-to-r from-[rgb(43,147,208)] via-[#f5a800] to-transparent rounded-full mt-2"></div>

                {/* Subheading with enhanced styling */}
                <p className="text-xl md:text-2xl text-gray-700 mt-4 max-w-2xl mx-auto md:mx-0 leading-relaxed font-medium drop-shadow-sm">
                  A strategic global partnership for MSMEs development
                </p>

                {/* Decorative element - small badge effect (visual only, no content) */}
                <div className="absolute -top-6 -right-6 w-12 h-12 bg-gradient-to-br from-[rgb(43,147,208)] to-[#f5a800] rounded-full opacity-10 blur-md pointer-events-none"></div>
              </div>

              {/* Original commented code preserved exactly as provided */}
              {/*                 
                <div className="flex mt-6 gap-4">
                  <button className="px-6 py-3 bg-[rgb(43,147,208)] text-white rounded-full hover:bg-[rgb(33,127,188)] transition-colors shadow-lg hover:shadow-xl">
                    Explore Our Impact
                  </button>
                  <button className="px-6 py-3 border border-[rgb(43,147,208)] text-[rgb(43,147,208)] rounded-full hover:bg-[rgb(43,147,208)] hover:text-white transition-colors">
                    View UN Documents
                  </button>
                </div> */}
            </div>
          </div>

          {/* Custom animations */}
          <style>{`
    @keyframes blob {
      0% {
        transform: translate(0px, 0px) scale(1);
      }
      33% {
        transform: translate(30px, -50px) scale(1.1);
      }
      66% {
        transform: translate(-20px, 20px) scale(0.9);
      }
      100% {
        transform: translate(0px, 0px) scale(1);
      }
    }
    
    @keyframes float {
      0% {
        transform: translateY(0px);
      }
      50% {
        transform: translateY(-10px);
      }
      100% {
        transform: translateY(0px);
      }
    }
    
    @keyframes wave {
      0% {
        transform: translateX(0);
      }
      50% {
        transform: translateX(-20px);
      }
      100% {
        transform: translateX(0);
      }
    }
    
    .animate-blob {
      animation: blob 7s infinite;
    }
    
    .animate-float {
      animation: float 6s ease-in-out infinite;
    }
    
    .animate-wave {
      animation: wave 8s ease-in-out infinite;
    }
    
    .animation-delay-1000 {
      animation-delay: 1s;
    }
    
    .animation-delay-1500 {
      animation-delay: 1.5s;
    }
    
    .animation-delay-2000 {
      animation-delay: 2s;
    }
    
    .animation-delay-2500 {
      animation-delay: 2.5s;
    }
    
    .animation-delay-4000 {
      animation-delay: 4s;
    }
    
    /* Smooth hover transitions for gradient text */
    .bg-gradient-to-r {
      transition: all 0.3s ease;
    }
  `}</style>
        </div>

        {/* Stats bar - counters */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard value={42} label="Years of UN Consultative Status" suffix="+" />
            <StatCard value={9} label="UN Bodies & Agencies" />
            <StatCard value={120} label="Countries Reached" suffix="+" />
            <StatCard value={15} label="Annual UN Events" />
          </div>
        </div>

        {/* UN Affiliations - Logo Grid */}
        <div
          ref={addToRefs}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 opacity-0 translate-y-10 transition-all duration-700"
        >
          <h2 className="text-3xl md:text-4xl font-light text-[rgb(43,147,208)] mb-12 text-center">
            United Nations <span className="text-[#f5a800]">Family</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {unAgencies.map((agency, idx) => (
              <AgencyLogo
                key={idx}
                Icon={agency.icon}
                name={agency.name}
                description={agency.desc}
              />
            ))}
          </div>
        </div>

        {/* International Partners */}
        <div
          ref={addToRefs}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-gray-50 rounded-3xl opacity-0 translate-y-10 transition-all duration-700"
        >
          <h2 className="text-3xl md:text-4xl font-light text-[rgb(43,147,208)] mb-10 text-center">
            Key International <span className="text-[#f5a800]">Partners</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {internationalPartners.map((partner, idx) => (
              <PartnerCard key={idx} name={partner.name} description={partner.desc} />
            ))}
          </div>
        </div>

        {/* Impact Section */}
        <div
          ref={addToRefs}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 opacity-0 translate-y-10 transition-all duration-700"
        >
          <h2 className="text-3xl md:text-4xl font-light text-[rgb(43,147,208)] mb-12 text-center">
            Driving Global <span className="text-[#f5a800]">MSME Impact</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactPoints.map((point, idx) => (
              <div
                key={idx}
                className="group bg-white border border-gray-100 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="text-[rgb(43,147,208)] mb-5 p-3 bg-[rgb(43,147,208)]/5 rounded-xl inline-block group-hover:scale-110 transition-transform">
                  {point.icon}
                </div>
                <h3 className="text-xl font-medium text-[rgb(43,147,208)] mb-2">
                  {point.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{point.desc}</p>
                <div className="w-12 h-1 bg-[#f5e56b] mt-6 rounded-full group-hover:w-20 transition-all" />
              </div>
            ))}
          </div>
        </div>

        {/* WASME & SDG Section with image and detailed text */}
        <div
          ref={addToRefs}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 opacity-0 translate-y-10 transition-all duration-700"
        >
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
            <div className="grid md:grid-cols-2 gap-8 p-8 md:p-12">
              {/* Text content */}
              <div className="space-y-4 text-gray-700">
                <h2 className="text-3xl md:text-4xl font-light text-[rgb(43,147,208)] mb-4">
                  WASME & the <span className="text-[#f5a800]">2030 Agenda</span>
                </h2>
                <p className="leading-relaxed">
                  The World Association for Small and Medium Enterprises (WASME) holds a unique
                  and distinguished position as the only international NGO with consultative
                  and observer status across a wide spectrum of United Nations agencies and
                  global intergovernmental organizations. For over four decades, WASME has
                  served as a vital bridge between the world's MSMEs and the international
                  policy-making ecosystem, ensuring that the voices, needs and aspirations of
                  small and medium enterprises are actively represented at the highest global
                  platforms.
                </p>
                <p className="leading-relaxed">
                  The Sustainable Development Goals (SDGs) are a set of 17 goals that were
                  adopted by the United Nations in 2015. The goals are designed to be a
                  blueprint for a more sustainable and equitable world by 2030. Achieving the
                  SDGs will require a global partnership between individuals, multinationals
                  and small and medium-sized enterprises (MSMEs).
                </p>
                <p className="leading-relaxed">
                  While the SDGs are a global effort, MSMEs may face more challenges when it
                  comes to adapting to sustainable development. This is due to their limited
                  resources and capacity, as well as their typically higher reliance on
                  traditional, unsustainable business models. However, there are also many
                  opportunities for MSMEs to contribute to the SDGs, such as through energy
                  efficiency improvements, the development of sustainable products and services
                  and the provision of employment and training opportunities in local communities.
                </p>
              </div>
              {/* Image */}
              <div className="flex items-center justify-center">
                <img
                  src="/img/initiatives.jpg"
                  alt="WASME global impact"
                  className="rounded-2xl shadow-lg object-cover w-full h-auto max-h-96"
                />
              </div>
            </div>
            {/* SDG Pills */}
            <div className="bg-gradient-to-r from-[#f0f7ff] to-[#fff6e5] p-8 md:p-12">
              <h3 className="text-2xl font-light text-[rgb(43,147,208)] mb-6">
                Advancing the Goals
              </h3>
              <div className="flex flex-wrap gap-4">
                <SDGPill
                  goal="SDG 8"
                  title="Decent Work & Economic Growth"
                  color="bg-[rgb(43,147,208)] text-white"
                />
                <SDGPill
                  goal="SDG 9"
                  title="Industry, Innovation & Infrastructure"
                  color="bg-[#f5e56b] text-gray-800"
                />
                <SDGPill
                  goal="SDG 17"
                  title="Partnerships for the Goals"
                  color="bg-[rgb(43,147,208)] text-white"
                />
                <SDGPill goal="SDG 1" title="No Poverty" color="bg-red-500 text-white" />
                <SDGPill
                  goal="SDG 5"
                  title="Gender Equality"
                  color="bg-orange-400 text-white"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default WASMEPartnershipPage;